import { Head, router, useForm, usePage } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '../Layout'

interface DeviceToken {
  id: number
  device_name: string
  device_type: string
  created_at: string
  last_seen_at: string | null
  expires_at: string
  print_jobs_count: number
  online: boolean
}

interface PageProps {
  devices: DeviceToken[]
  newly_created_token?: string | null
  account?: { id: number; name: string; slug: string }
}

export default function Index({ devices = [], newly_created_token = null, account }: PageProps) {
  const { props: pageProps } = usePage<{ flash?: { notice?: string; alert?: string } }>()
  const { flash } = pageProps
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [copied, setCopied] = useState(false)

  const createForm = useForm({
    device_name: '',
    device_type: 'thermal_80mm'
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createForm.post(window.location.pathname, {
      onSuccess: () => {
        setShowCreateModal(false)
        createForm.reset()
      }
    })
  }

  const handleRevoke = (device: DeviceToken) => {
    if (!confirm(`¿Revocar la impresora "${device.device_name}"? Ya no podrá recibir trabajos.`)) {
      return
    }
    router.delete(`/${account.slug}/devices/${device.id}`, {
      onSuccess: () => {
        // La lista se refresca sola vía Inertia; nada más que hacer.
      }
    })
  }

  const handleTestPrint = (device: DeviceToken) => {
    if (!confirm(`¿Enviar ticket de prueba a "${device.device_name}"?`)) {
      return
    }
    router.post(`/${account.slug}/devices/${device.id}/test_print`, {}, {
      preserveScroll: true,
    })
  }

  const handleReprintLast = (device: DeviceToken) => {
    if (!confirm(`¿Reimprimir el último ticket en "${device.device_name}"?`)) {
      return
    }
    router.post(`/${account.slug}/devices/${device.id}/reprint_last`, {}, {
      preserveScroll: true,
    })
  }

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca'
    const d = new Date(dateStr)
    return d.toLocaleString('es-HN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const deviceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'thermal_80mm': 'Térmica 80mm',
      'thermal_58mm': 'Térmica 58mm',
      'thermal_76mm': 'Térmica 76mm'
    }
    return labels[type] || type
  }

  return (
    <Layout account={account}>
      <Head title="Impresoras" />

      <div className="px-4 sm:px-6 lg:px-8">
        {/* Banner del token recién creado */}
        {newly_created_token && (
          <div className="mb-4 rounded-lg border-2 border-blue-300 bg-blue-50 p-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <h3 className="font-semibold text-blue-900">Token generado — cópialo ahora</h3>
              </div>
              <p className="text-sm text-blue-700">
                Este token se usa para conectar la aplicación de impresión. Por seguridad no se mostrará de nuevo.
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-sm text-gray-900 border border-blue-200 break-all">
                  {newly_created_token}
                </code>
                <button
                  onClick={() => handleCopyToken(newly_created_token)}
                  className="inline-flex items-center justify-center gap-1 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {copied ? '✓ Copiado' : 'Copiar token'}
                </button>
              </div>
            </div>
          </div>
        )}

        {flash?.notice && (
          <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {flash.alert}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🖨️ Impresoras</h1>
            <p className="mt-1 text-sm text-gray-600">
              Gestiona las aplicaciones de impresión conectadas a tu cuenta
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center gap-1 rounded bg-[#202020] px-4 py-2 text-sm font-medium text-white hover:bg-[#333]"
          >
            <span>+</span> Agregar impresora
          </button>
        </div>

        {devices.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay impresoras registradas</h3>
            <p className="mt-1 text-sm text-gray-500">
              Agrega una impresora para recibir tickets automáticamente desde el POS.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow sm:rounded-lg divide-y divide-gray-200">
            {devices.map((device) => (
              <div key={device.id} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full ${device.online ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{device.device_name}</h3>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${device.online ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {device.online ? '● En línea' : '○ Sin conexión'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-500">
                      {deviceTypeLabel(device.device_type)} • {device.print_jobs_count} impresiones
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      Última actividad: {formatDateTime(device.last_seen_at)} • Expira: {formatDateTime(device.expires_at)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleTestPrint(device)}
                    disabled={!device.online}
                    className="inline-flex items-center justify-center gap-1 rounded border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={device.online ? 'Enviar ticket de prueba a esta impresora' : 'Dispositivo no está conectado'}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Probar
                  </button>
                  <button
                    onClick={() => handleReprintLast(device)}
                    disabled={!device.online}
                    className="inline-flex items-center justify-center gap-1 rounded border border-green-200 bg-white px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={device.online ? 'Reimprimir el último ticket emitido' : 'Dispositivo no está conectado'}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reimprimir último
                  </button>
                  <button
                    onClick={() => handleRevoke(device)}
                    className="inline-flex items-center justify-center gap-1 rounded border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    Revocar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal crear impresora */}
        {showCreateModal && (
          <>
            <div className="fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setShowCreateModal(false)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <form onSubmit={handleCreate} className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Nueva impresora</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <input
                        type="text"
                        value={createForm.data.device_name}
                        onChange={(e) => createForm.setData('device_name', e.target.value)}
                        placeholder="Ej: Caja Principal, Cocina, Bodega"
                        className="w-full rounded border-gray-300 shadow-sm focus:border-[#55ACEE] focus:ring-[#55ACEE] text-sm p-2 border"
                        required
                      />
                      {createForm.errors.device_name && (
                        <p className="mt-1 text-sm text-red-600">{createForm.errors.device_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select
                        value={createForm.data.device_type}
                        onChange={(e) => createForm.setData('device_type', e.target.value)}
                        className="w-full rounded border-gray-300 shadow-sm focus:border-[#55ACEE] focus:ring-[#55ACEE] text-sm p-2 border"
                      >
                        <option value="thermal_80mm">Térmica 80mm (estándar)</option>
                        <option value="thermal_58mm">Térmica 58mm</option>
                        <option value="thermal_76mm">Térmica 76mm</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={createForm.processing}
                      className="rounded bg-[#202020] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#333] disabled:opacity-50"
                    >
                      {createForm.processing ? 'Creando...' : 'Crear'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
